import React, {useCallback, useEffect, useLayoutEffect, useRef} from 'react';

// Modules
import type {FunctionComponent} from 'react';
import {useRouter} from 'next/router';

// Config
import {config} from '@modules/config';
import {useUser} from '@deezer/react-user';
import type {
	ConsentScreenApi,
	ConsentSettings,
	DzrGDPRInitOptions,
} from '@tempo/cookie';
import {insertTCFScript, initGDPRCallback, useUserConsent} from '@tempo/cookie';
import {getISOLocaleFromLocale} from '@deezer/deezer-compatibility';
import {getCDP} from '@app/modules/tracking/cdp';

const UA_BOT_PATTERN = config.get('botCrawler');
const uaBotRegex = new RegExp(UA_BOT_PATTERN, 'i');

declare global {
	interface Window {
		// TODO: fix my types in tempo to remove the unknown
		consentScreenApi?: Omit<ConsentScreenApi, 'update'> & {
			update: (args: unknown) => void;
		};
		onDzrGDPRReady?: (initApi: () => Promise<ConsentScreenApi>) => unknown;
		__tcfapi?: (
			command: string,
			version: number,
			callback: (
				tcData: {eventStatus: string; listenerId: number},
				success: boolean,
			) => void,
			listenerId?: number,
		) => void;
		didomiEventListeners?: Array<{event: string; listener: () => void}>;
		dataLayer?: Record<string, unknown>[];
	}
}

type DidomiAction = 'acceptance' | 'refuse' | 'validate-parameters';

function getConsentLabel(
	statistics: boolean,
	targetedAdvertising: boolean,
): string {
	if (statistics && targetedAdvertising) return 'opt-in';
	if (statistics && !targetedAdvertising) return 'opt-out_targeted-ads';
	if (!statistics && targetedAdvertising) return 'opt-out_statistics';
	return 'opt-out_statistics_targeted-ads';
}

function pushDataLayer(payload: Record<string, string | boolean>) {
	if ('dataLayer' in window && Array.isArray(window.dataLayer)) {
		window.dataLayer.push(payload);
	}
}

export const useOpenConsentScreen = () => {
	return useCallback(() => {
		if (typeof window === 'undefined') return;
		window.consentScreenApi?.openPreferences(2, 'purposes');
	}, []);
};

type ConsentBannerProps = {locale: string; userId: string; country?: string};

const ConsentBanner: FunctionComponent<ConsentBannerProps> = ({
	locale,
	userId,
	country,
}) => {
	const {setConsentSettings} = useUserConsent();
	const setConsentSettingsRef = useRef(setConsentSettings);
	const userIdRef = useRef(userId);
	const localeRef = useRef(locale);
	const countryRef = useRef(country);
	// Always holds the latest consent values. Read by preferences.hidden fallback
	// to push validate-parameters when Enregistrer is clicked.
	const latestConsentRef = useRef<ConsentSettings | null>(null);

	useLayoutEffect(() => {
		setConsentSettingsRef.current = setConsentSettings;
		userIdRef.current = userId;
		localeRef.current = locale;
		countryRef.current = country;
	});

	useEffect(() => {
		let tcfListenerId: number | null = null;
		// Set by TCF useractioncomplete, consumed in onConsentUpdate for CDP tracking.
		let pendingConsentTrack = false;
		// Set by Didomi click events before onConsentUpdate fires.
		// Consumed in onConsentUpdate so the GTM push uses fresh consent values.
		let pendingDidomiAction: DidomiAction | null = null;
		// Didomi fires onConsentUpdate once on init with saved/default consent.
		// We skip that call and treat every subsequent one without pendingDidomiAction
		// as the "Enregistrer" save action from the preferences panel.
		let isFirstConsentLoad = true;

		window.didomiEventListeners = window.didomiEventListeners ?? [];

		window.didomiEventListeners.push({
			event: 'notice.shown',
			listener: () => {
				pushDataLayer({
					event: 'click',
					eventcategory: 'cookies-banner',
					eventaction: 'display',
				});
			},
		});
		window.didomiEventListeners.push({
			event: 'notice.clickagree',
			listener: () => {
				pendingDidomiAction = 'acceptance';
			},
		});
		window.didomiEventListeners.push({
			event: 'notice.clickdisagree',
			listener: () => {
				pendingDidomiAction = 'refuse';
			},
		});
		// Opening preferences from the notice fires notice.clickdisagree as a side
		// effect. Reset so the spurious refuse doesn't get consumed in onConsentUpdate.
		window.didomiEventListeners.push({
			event: 'preferences.shown',
			listener: () => {
				pendingDidomiAction = null;
			},
		});
		window.didomiEventListeners.push({
			event: 'preferences.clickagreetoall',
			listener: () => {
				pendingDidomiAction = 'acceptance';
			},
		});
		window.didomiEventListeners.push({
			event: 'preferences.clickdisagreetoall',
			listener: () => {
				pendingDidomiAction = 'refuse';
			},
		});

		window.onDzrGDPRReady = (initApi) => {
			initGDPRCallback(
				initApi as (options?: DzrGDPRInitOptions) => Promise<ConsentScreenApi>,
				{
					...(countryRef.current === 'BE' && {cmpType: 'didomi'}),
					lang: localeRef.current,
					darkMode: false,
					userID: userIdRef.current,
					cookieDomain: config.get('cookieDomain'),
					gatewayHost: config.get('deezerWebsiteUrl'),
					onConsentUpdate: (consent: ConsentSettings) => {
						latestConsentRef.current = consent;
						setConsentSettingsRef.current(consent);
						getCDP().setConsent(consent);
						if (pendingConsentTrack) {
							pendingConsentTrack = false;
							const uid = userIdRef.current;
							getCDP().logEvent({
								type: 'track',
								uid,
								eventName: 'Segment Consent Preference Updated',
								userId: uid,
							});
						}
						if (countryRef.current !== 'BE') return;
						const label = getConsentLabel(
							!!consent.statistics,
							!!consent.targetedAdvertising,
						);
						if (pendingDidomiAction) {
							if (pendingDidomiAction === 'acceptance') {
								pushDataLayer({
									event: 'click',
									eventcategory: 'cookies-banner',
									eventaction: 'acceptance',
								});
							} else if (pendingDidomiAction === 'refuse') {
								pushDataLayer({
									event: 'click',
									eventcategory: 'cookies-banner',
									eventaction: 'refuse',
									eventlabel: label,
								});
							} else {
								pushDataLayer({
									event: 'click',
									'non-interactional': 'true',
									eventcategory: 'cookies-banner',
									eventaction: 'validate-parameters',
									eventlabel: label,
								});
							}
							pendingDidomiAction = null;
						} else if (isFirstConsentLoad) {
							// Skip the automatic call on init — not a user action.
							isFirstConsentLoad = false;
						} else {
							// No pending action and not the first load → "Enregistrer".
							pushDataLayer({
								event: 'click',
								'non-interactional': 'true',
								eventcategory: 'cookies-banner',
								eventaction: 'validate-parameters',
								eventlabel: label,
							});
						}
					},
				},
			);

			window.__tcfapi?.('addEventListener', 2, (tcData, success) => {
				if (tcfListenerId === null) {
					tcfListenerId = tcData.listenerId;
				}
				if (success && tcData.eventStatus === 'useractioncomplete') {
					pendingConsentTrack = true;
				}
			});
		};

		insertTCFScript({
			id: 'gdpr-web-script',
			scriptUrl: `${config.get('commonAssetsUrl')}/js/gdpr-appliance.js`,
		});

		return () => {
			if (tcfListenerId !== null) {
				window.__tcfapi?.('removeEventListener', 2, () => {}, tcfListenerId);
			}
		};
	}, []);

	return null;
};

export const ConsentScreen: FunctionComponent = () => {
	const {locale} = useRouter();
	const [user] = useUser();

	if (!user || uaBotRegex.test(navigator.userAgent)) {
		return null;
	}

	return (
		<ConsentBanner
			locale={getISOLocaleFromLocale(locale)}
			userId={user.id}
			country={user.country}
		/>
	);
};
